import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Client } from '@/api/clients/clientsApi.types';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { useUpdateOwnProfileMutation } from '@/hooks/useClientPortal';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';
import { EditDetailsDialog } from '../EditDetailsDialog';

interface DetailsSubsectionProps {
  profile: Client;
}

export function DetailsSubsection({ profile }: DetailsSubsectionProps) {
  const { t } = useTranslation('clients');
  const updateMutation = useUpdateOwnProfileMutation();
  const [isEditing, setIsEditing] = useState(false);
  const rows = [
    { label: t('form.email'), value: profile.email },
    {
      label: t('form.phone'),
      value: profile.phone === null ? null : formatPhoneNumberForDisplay(profile.phone),
    },
    { label: t('form.city'), value: profile.city },
  ];

  function save(phone: string | null, city: string | null) {
    updateMutation.mutate(
      { phone, city },
      {
        onSuccess: function done() {
          showSuccessNotification(t('yourDetails.saved'));
          setIsEditing(false);
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('yourDetails.saveError'));
        },
      }
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-xs font-bold text-zoomuted tracking-wider uppercase">
          {t('yourDetails.title')}
        </h3>
        <Button
          variant="outline"
          size="chip"
          onClick={function openEditor() {
            setIsEditing(true);
          }}
        >
          <Icon name="Edit" />
          {t('yourDetails.edit')}
        </Button>
      </div>
      <dl className="space-y-1.5">
        {rows.map(function renderRow(row) {
          return (
            <div key={row.label} className="flex items-baseline justify-between gap-3 text-[13px]">
              <dt className="text-zoomuted shrink-0">{row.label}</dt>
              <dd className="text-zooindigo text-right truncate">
                {row.value === null || row.value.length === 0 ? '—' : row.value}
              </dd>
            </div>
          );
        })}
      </dl>
      {isEditing ? (
        <EditDetailsDialog
          profile={profile}
          isSaving={updateMutation.isPending}
          onSave={save}
          onCancel={function closeEditor() {
            setIsEditing(false);
          }}
        />
      ) : null}
    </div>
  );
}
